import { useMemo, useState } from 'react';
import { Formik, Form } from 'formik';
import { Button, CheckboxField, FormField } from '@neshane/blueberry';
import { FormikInput, JalaliDatePicker } from '../../../../common/forms';
import { profileSchema } from '../../../../common/validation';
import { TermsModal, useTermsConsent } from '../../../Terms';
import styles from './ProfileStep.module.css';

/**
 * Profile Step Component
 * Third step of user login - complete user profile (new-user registration).
 *
 * The legal-consent checkbox is disabled: clicking it opens a scroll-to-accept
 * terms & conditions modal. The checkbox is checked only after the user reaches
 * the end of the terms and approves. Acceptance is recorded by the backend
 * during profile completion.
 */
function ProfileStep({ onSubmit, loading = false }) {
    const [consentAccepted, setConsentAccepted] = useState(false);
    const [termsOpen, setTermsOpen] = useState(false);
    const {
        terms,
        loading: termsLoading,
        error: termsError,
        loadTerms,
    } = useTermsConsent();

    const openTerms = async () => {
        setTermsOpen(true);
        if (!terms) {
            await loadTerms();
        }
    };

    const handleApproveTerms = () => {
        setConsentAccepted(true);
        setTermsOpen(false);
    };

    const maxBirthDate = useMemo(() => {
        const now = new Date();
        return new Date(now.getFullYear() - 18, now.getMonth(), now.getDate());
    }, []);

    const handleSubmit = (values, formikBag) => {
        if (!consentAccepted) return;
        onSubmit({ ...values, consentAccepted }, formikBag);
    };

    return (
        <Formik
            initialValues={{
                firstName: '',
                lastName: '',
                nationalCode: '',
                birthDate: '',
            }}
            validationSchema={profileSchema}
            onSubmit={handleSubmit}
        >
            {({ isSubmitting, isValid, values, errors, touched, setFieldValue }) => (
                <Form>
                    <FormikInput
                        name="nationalCode"
                        type="text"
                        label="کد ملی"
                        placeholder="0000000000"
                        maxLength={10}
                        disabled={loading || isSubmitting}
                        required
                        autoFocus tooltip="کد ملی - 0000000000" />

                    <div className="form-row">
                        <FormikInput
                            name="firstName"
                            type="text"
                            label="نام"
                            placeholder="نام"
                            disabled={loading || isSubmitting}
                            required tooltip="نام را وارد کنید" />

                        <FormikInput
                            name="lastName"
                            type="text"
                            label="نام خانوادگی"
                            placeholder="نام خانوادگی"
                            disabled={loading || isSubmitting}
                            required tooltip="نام خانوادگی را وارد کنید" />
                    </div>

                    <FormField
                        label="تاریخ تولد (شمسی) *"
                        htmlFor="birthDate"
                        alertMessage={touched.birthDate && errors.birthDate ? errors.birthDate : undefined}
                    >
                        <JalaliDatePicker
                            id="birthDate"
                            name="birthDate"
                            placeholder="1370/01/01"
                            value={values.birthDate}
                            isDisabled={loading || isSubmitting}
                            maxDate={maxBirthDate}
                            onChange={(value) => setFieldValue('birthDate', value || '')}
                            inputClass={touched.birthDate && errors.birthDate ? 'error' : ''}
                        />
                    </FormField>

                    <div className={styles.checkbox} onClick={openTerms} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openTerms(); } }}>
                        <CheckboxField
                            isChecked={consentAccepted}
                            isDisabled={!consentAccepted || loading || isSubmitting}
                            onChange={() => { }}
                            label="رضایت قانونی خود را برای انجام بررسی‌ها اعلام می‌کنم"
                        />
                    </div>

                    <Button
                        type="submit"
                        variant="primaryBrand"
                        text={loading || isSubmitting ? 'در حال ثبت...' : 'تکمیل ثبت‌نام'}
                        isLoading={loading || isSubmitting}
                        disabled={loading || isSubmitting || !isValid || !consentAccepted}
                        fit="parent"
                    />

                    <TermsModal
                        isOpen={termsOpen}
                        onClose={() => setTermsOpen(false)}
                        terms={terms}
                        loading={termsLoading}
                        error={termsError}
                        onApprove={handleApproveTerms}
                        approveText="تایید و ثبت‌نام"
                    />
                </Form>
            )}
        </Formik>
    );
}

export default ProfileStep;
